/**
 * Shipping Date Calculator
 * 
 * 纯函数式日期计算模块，负责根据节假日配置计算下一个发货日期。
 * 
 * 设计原则：
 * - 零依赖，只使用原生 JavaScript Date API
 * - 不包含文案逻辑，只负责日期计算
 * - 所有函数为纯函数，便于测试
 * 
 * @module shippingCalculator
 */

import type { HolidayConfig } from '@/data/config';

/**
 * 判定某天是否为非工作日（周末或节假日）
 * 
 * @param date - 要判断的日期
 * @param holidays - 节假日配置（包含 recurring 和 specific 两类）
 * @returns true 表示非工作日，false 表示工作日
 * 
 * @example
 * const config = { recurring: ['12-25'], specific: ['2025-12-24'] };
 * isNonWorkingDay(new Date('2025-12-24'), config); // true (specific)
 * isNonWorkingDay(new Date('2025-12-25'), config); // true (recurring)
 * isNonWorkingDay(new Date('2025-12-21'), config); // true (周六)
 * isNonWorkingDay(new Date('2025-12-23'), config); // false (工作日)
 */
export function isNonWorkingDay(date: Date, holidays: HolidayConfig): boolean {
    const day = date.getDay();

    // 周日(0)、周六(6) 不发货
    if (day === 0 || day === 6) {
        return true;
    }

    // 格式化日期为 MM-DD 和 YYYY-MM-DD
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const dayOfMonth = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear();

    const mmdd = `${month}-${dayOfMonth}`;           // 例: "12-25"
    const yyyymmdd = `${year}-${month}-${dayOfMonth}`; // 例: "2025-12-25"

    // 检查是否在循环节日列表中（每年重复）
    if (holidays.recurring.includes(mmdd)) {
        return true;
    }

    // 检查是否在特定日期节日列表中
    if (holidays.specific.includes(yyyymmdd)) {
        return true;
    }

    return false;
}

/**
 * 获取下一个发货日期（以 16:00 为截点，超过则从后一天开始）
 * 
 * 逻辑：
 * 1. 从"明天"开始查找；若当前时间超过 16:00，则从后一天开始
 * 2. 跳过所有非工作日（周末 + 节假日）
 * 3. 返回找到的第一个工作日（UTC 0点）
 * 
 * @param holidays - 节假日配置
 * @param cutoffHour - 发货截单时间 (默认 16)，超过此时间则从后一天开始计算
 * @returns 下一个发货日期（Date 对象，时间为当天 00:00:00）
 * 
 * @example
 * // 假设今天是 2025-12-20（周五），圣诞假期为 12-24、12-25
 * const config = { recurring: [], specific: ['2025-12-24', '2025-12-25'] };
 * const nextDate = getNextShippingDate(config);
 * // 返回: 2025-12-26（周五：跳过 → 周六21、周日22、周一23工作日 → 实际下个工作日是周一23...
 * // 等等，让我重新理解：从"明天"21号开始
 * // 21号（六）非工作日 → 22号（日）非工作日 → 23号（一）工作日 ✓
 */
export function getNextShippingDate(holidays: HolidayConfig, cutoffHour = 16): Date {
    // 以 cutoffHour (默认16:00) 为截点，超过则从后一天开始
    const checkDate = new Date();
    const startOffset = checkDate.getHours() >= cutoffHour ? 2 : 1;
    checkDate.setDate(checkDate.getDate() + startOffset);
    checkDate.setHours(0, 0, 0, 0);  // 重置为当天 0 点

    // 安全计数器，防止死循环（理论上最长假期不超过 30 天）
    let safetyCount = 0;
    const MAX_ITERATIONS = 30;

    while (isNonWorkingDay(checkDate, holidays) && safetyCount < MAX_ITERATIONS) {
        checkDate.setDate(checkDate.getDate() + 1);
        safetyCount++;
    }

    // 如果超过安全上限，说明配置异常（例如：未来 30 天都被设为假日）
    // 此时仍返回最后一个日期，由调用方处理异常情况
    if (safetyCount >= MAX_ITERATIONS) {
        console.warn(
            '[ShippingCalculator] 未来 30 天内未找到工作日，可能配置异常。',
            'Holidays config:',
            holidays
        );
    }

    return checkDate;
}

/**
 * 计算目标日期距离今天还有几天。
 * @param date - 目标日期。
 * @returns 天数差 (向上取整)。
 */
export function getDaysUntil(date: Date): number {

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const target = new Date(date);
    target.setHours(0, 0, 0, 0);

    const diffTime = target.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}
